import CTA from 'components/UI/Cta';
import RichText from 'components/UI/Richtext';
import Image from 'components/UI/Image';
import { ReactElement } from 'react';
import { IComponentHeroInlineCircleFields } from 'types/contentful';
import Vimeo, { isVimeoVideoGuard } from 'components/UI/Videos/Vimeo';
import YouTube, { isYoutubeVideoGuard } from 'components/UI/Videos/Youtube';
import FreshpaintVideo, { isFreshpaintVideoGuard } from 'components/UI/Videos/Freshpaint';
import ContentfulVideo from 'components/UI/Videos/ContentfulVideo';
import { joinString } from 'lib/util';
import { LINK_CONTENT_TYPE } from 'lib/consts';
import IFrameCTA from 'components/UI/IFrameCta';

export default function HeroInlineCircle({
  heading,
  colorScheme,
  image,
  video,
  description,
  cta,
}: IComponentHeroInlineCircleFields): ReactElement {
  const [background, highlight] = colorScheme.split('-');

  const Comp = (): ReactElement | null => {
    if (!video) return null;
    if (isVimeoVideoGuard(video)) {
      return <Vimeo video={video} />;
    } else if (isYoutubeVideoGuard(video)) {
      return <YouTube video={video} />;
    } else if (isFreshpaintVideoGuard(video)) {
      return (
        <div className="relative w-[1030px] h-full" style={{ aspectRatio: '16/9' }}>
          <FreshpaintVideo video={video} showControls={false} isMuted classNames="z-10" />
          <div className="absolute inset-0 bg-black/50 z-20" />
        </div>
      );
    } else {
      return <ContentfulVideo video={video} />;
    }
  };

  const noImageOrVideo = !image && !video;

  return (
    <div
      className={`flex flex-col md:flex-row bg-${background} overflow-hidden ${
        !image ? 'pt-5 pb-5 md:pb-16' : ''
      }`}
    >
      <div
        className={`flex-initial md:self-center w-full py-10 pl-site-component ${
          noImageOrVideo ? '' : 'lg:w-1/2'
        }`}
      >
        <RichText
          document={heading}
          highlight={highlight}
          classNames={`font-heading text-4xl md:text-5xl text-${background} ${
            noImageOrVideo ? '' : 'max-w-725'
          }`}
        />
        {description && (
          <RichText
            document={description}
            classNames="py-4 text-lg lg:text-2xl text-primary-actual"
          />
        )}
        <div className="w-3/4 lg:w-full text-5xl">
          {cta &&
            (cta.sys.contentType.sys.id === LINK_CONTENT_TYPE.fragmentIFrameCta ? (
              <IFrameCTA {...cta.fields} backgroundColor="black" />
            ) : (
              <CTA {...cta.fields} classNames="mt-6" />
            ))}
        </div>
      </div>

      {image && (
        <div className="relative flex-auto w-full lg:w-1/2 h-banner-mobile">
          {video ? (
            <Comp />
          ) : (
            <Image
              image={image}
              classNames="w-full h-full object-cover z-0"
              query="fit=fill&f=center&h=500&w=800"
            />
          )}

          {/* Ellipse SVG on top */}
          <svg
            viewBox="0 0 75 500"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={joinString([
              `hidden md:block fill-current absolute left-0 top-0 text-${background}-actual h-full z-10`,
              video && isFreshpaintVideoGuard(video) ? 'w-[90px]' : 'w-20',
            ])}
          >
            <ellipse cx="-448" cy="250.5" rx="523" ry="523.5" />
          </svg>
        </div>
      )}
    </div>
  );
}
